import { sentryService } from '@/services/telemetry/sentry';
import { PubSub } from '@/shared/lib/pub-sub';
import { reactive } from '@/shared/lib/reactive';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import { portraitGeneration } from './PortraitGeneration';

export class KioskStyle extends PubSub {
  EVENTS = {
    INITIALIZED: 'INITIALIZED',
  };

  @reactive
  styles = [];

  @reactive
  selectedStyleId = null;

  @reactive
  enableSelector = null;

  constructor() {
    super();
    exposeToGlobalScope(this, 'KioskStyle');
  }

  /** @param {boolean | null | undefined} [enableSelector] */
  prepare(enableSelector = null) {
    this.enableSelector = enableSelector;
    this.selectedStyleId = null;
  }

  /**
   * @param {import('@/shared/types/api').StyleResponse[]} styles
   * @param {boolean | null | undefined} [enableSelector]
   */
  setStyles(styles, enableSelector = null) {
    this.enableSelector = enableSelector;
    this.styles = styles;
    if (!this.getStyleById(this.selectedStyleId)) {
      this.selectedStyleId = null;
    }

    sentryService.addBreadcrumb(
      'kiosk.style',
      'Kiosk styles initialized successfully',
      {
        styleCount: styles.length,
        styleIds: styles.map((s) => s.id),
      }
    );

    sentryService.setContext('kiosk_styles', {
      available_styles: styles.map((s) => ({ id: s.id, title: s.title })),
      style_count: styles.length,
      selector_enabled: enableSelector,
    });

    // Fire INITIALIZED synchronously rather than deferring to nextTick.
    // Deferring creates a window where this.styles is set but INITIALIZED
    // hasn't fired, and reset() between assignment and nextTick would fire
    // the event with stale data after reset.
    this.trigger(this.EVENTS.INITIALIZED, this.styles, true);
  }

  getStyleById(styleId) {
    return this.styles.find((style) => style.id === styleId);
  }

  selectStyle(styleId) {
    // Guard: don't select a style that doesn't exist in the loaded set.
    // This can happen if selectStyle is called while the styles change.
    const selectedStyle = this.getStyleById(styleId);
    if (!selectedStyle) {
      console.warn(
        `[KioskStyle] selectStyle called with unknown styleId: ${styleId}. ` +
          `Available: [${this.styles.map((s) => s.id).join(', ')}]`
      );
      sentryService.addBreadcrumb(
        'kiosk.style',
        'selectStyle called with unknown styleId',
        {
          styleId,
          availableIds: this.styles.map((s) => s.id),
        },
        'warning'
      );
      return;
    }

    const previousStyleId = this.selectedStyleId;

    // Reset portrait generation state when style changes
    if (previousStyleId !== styleId && previousStyleId !== null) {
      portraitGeneration.resetGenerationState();
    }

    this.selectedStyleId = styleId;

    sentryService.addBreadcrumb('kiosk.style', 'Style selected', {
      styleId,
      styleTitle: selectedStyle?.title,
      previousStyleId,
      wasStyleChange: previousStyleId !== null && previousStyleId !== styleId,
    });

    // Update Sentry context with currently selected style
    sentryService.setTag('selected_style_id', styleId);
    sentryService.setContext('selected_style', {
      id: styleId,
      title: selectedStyle?.title,
      previous_style_id: previousStyleId,
    });
  }

  clearSentryContext() {
    sentryService.setContext('kiosk_styles', null);
    sentryService.setContext('selected_style', null);
    sentryService.setTag('selected_style_id', null);
  }

  /**
   * Reset only user-specific state (selected style) between sessions.
   * Preserves kiosk-level config (styles[], enableSelector): KioskConfig
   * replaces those, only between sessions.
   */
  resetForNewSession() {
    this.selectedStyleId = null;

    // Clear Sentry selected style context but keep available styles context
    sentryService.setContext('selected_style', null);
    sentryService.setTag('selected_style_id', null);

    sentryService.addBreadcrumb(
      'kiosk.style',
      'KioskStyle selection reset for new session',
      {
        styles_preserved: this.styles.length,
      },
      'info'
    );
  }

  /**
   * Full reset - clears everything including kiosk config.
   * Only call this on kiosk reconfiguration, NOT between user sessions.
   */
  reset() {
    this.clearSentryContext();
    this.selectedStyleId = null;
    this.styles = [];
    this.enableSelector = null;

    // Clear PubSub state: the INITIALIZED event's ever-cache holds a reference
    // to the old styles array. Without this, code calling
    // ever(EVENTS.INITIALIZED, cb) after reset() would fire immediately
    // with stale data from the previous initialization.
    this.resetPubSub();

    sentryService.addBreadcrumb(
      'kiosk.style',
      'KioskStyle full reset',
      {},
      'info'
    );
  }
}

export const kioskStyle = new KioskStyle();
